/**
 * API client.
 *
 * One place that knows about the base URL, the bearer token, the error
 * envelope, and refresh-on-401. Every feature calls `api.get`/`api.post` and
 * never touches `fetch` directly, so retry and auth behaviour cannot differ
 * between screens.
 *
 * Types are hand-written for the proof of concept. The backend publishes an
 * OpenAPI schema at /openapi.json, so generating these with
 * `openapi-typescript` is the obvious next step and would remove the risk of
 * these drifting from the server.
 */

const BASE =
  (import.meta.env.VITE_API_BASE_URL as string | undefined)?.replace(/\/$/, "") ??
  "http://localhost:10100";

const ACCESS_KEY = "compendium.access";
const REFRESH_KEY = "compendium.refresh";

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly detail?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }

  /** True when the user needs to sign in again rather than retry. */
  get isAuthFailure() {
    return this.status === 401;
  }
}

export const tokens = {
  access: () => localStorage.getItem(ACCESS_KEY),
  refresh: () => localStorage.getItem(REFRESH_KEY),
  set(access: string, refresh: string) {
    localStorage.setItem(ACCESS_KEY, access);
    localStorage.setItem(REFRESH_KEY, refresh);
  },
  clear() {
    localStorage.removeItem(ACCESS_KEY);
    localStorage.removeItem(REFRESH_KEY);
  },
};

/**
 * Refresh is shared: several queries can fail with 401 at the same moment, and
 * each retrying its own refresh would spend the single-use refresh token
 * several times over and sign the user out.
 */
let refreshInFlight: Promise<boolean> | null = null;

async function refreshAccessToken(): Promise<boolean> {
  const refreshToken = tokens.refresh();
  if (!refreshToken) return false;

  refreshInFlight ??= (async () => {
    try {
      const response = await fetch(`${BASE}/api/v1/auth/refresh`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refreshToken }),
      });
      if (!response.ok) return false;
      const data = await response.json();
      tokens.set(data.accessToken, data.refreshToken);
      return true;
    } catch {
      return false;
    } finally {
      // Cleared on the next tick so concurrent callers all observe the same
      // settled promise before it is discarded.
      setTimeout(() => {
        refreshInFlight = null;
      }, 0);
    }
  })();

  return refreshInFlight;
}

type RequestOptions = {
  method?: string;
  body?: unknown;
  query?: Record<string, unknown>;
  signal?: AbortSignal;
  raw?: boolean;
};

function buildUrl(path: string, query?: Record<string, unknown>) {
  // BASE is empty in the single-origin production build, so this string is a
  // relative path. new URL() rejects a relative URL with no base and throws,
  // which took down every request before the first screen could render.
  const url = new URL(`${BASE}/api/v1${path}`, window.location.origin);
  for (const [key, value] of Object.entries(query ?? {})) {
    if (value === undefined || value === null || value === "") continue;
    if (Array.isArray(value)) {
      value.forEach((entry) => url.searchParams.append(key, String(entry)));
    } else {
      url.searchParams.set(key, String(value));
    }
  }
  return url.toString();
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const send = async (): Promise<Response> => {
    const headers: Record<string, string> = {};
    const access = tokens.access();
    if (access) headers.Authorization = `Bearer ${access}`;

    let body: BodyInit | undefined;
    if (options.body instanceof FormData) {
      body = options.body;
    } else if (options.body !== undefined) {
      headers["Content-Type"] = "application/json";
      body = JSON.stringify(options.body);
    }

    return fetch(buildUrl(path, options.query), {
      method: options.method ?? "GET",
      headers,
      body,
      signal: options.signal,
    });
  };

  let response = await send();

  if (response.status === 401 && tokens.refresh()) {
    if (await refreshAccessToken()) {
      response = await send();
    }
  }

  if (!response.ok) {
    let code = "error";
    let message = response.statusText || "Request failed";
    let detail: unknown;
    try {
      const payload = await response.json();
      code = payload?.error?.code ?? code;
      message = payload?.error?.message ?? message;
      detail = payload?.error?.detail;
    } catch {
      /* a non-JSON error body: keep the status text */
    }
    if (response.status === 401) tokens.clear();
    throw new ApiError(response.status, code, message, detail);
  }

  if (options.raw) return response as unknown as T;
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

/** One frame off a `text/event-stream`. */
export type ServerEvent = { event: string; data: unknown };

/**
 * POST, and read the response as server-sent events.
 *
 * Not `EventSource`, which can only GET and cannot carry an Authorization
 * header — it was built for public feeds, and this is a question about
 * restricted documents. So: `fetch`, a reader over the body, and a parser for
 * the four lines of the SSE grammar this application actually emits.
 *
 * Shares `request`'s auth, including the 401 refresh, by asking for the raw
 * Response and reading it here.
 */
export async function* streamEvents(
  path: string,
  body: unknown,
  signal?: AbortSignal,
): AsyncGenerator<ServerEvent> {
  const response = await request<Response>(path, {
    method: "POST",
    body,
    signal,
    raw: true,
  });
  const reader = response.body?.getReader();
  if (!reader) throw new ApiError(0, "no_stream", "The response carried no body");

  const decoder = new TextDecoder();
  let buffered = "";
  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      buffered += decoder.decode(value, { stream: true });
      // Frames are separated by a blank line. A partial frame stays in the
      // buffer: a chunk boundary can land anywhere, including mid-JSON.
      let split: number;
      while ((split = buffered.indexOf("\n\n")) !== -1) {
        const frame = buffered.slice(0, split);
        buffered = buffered.slice(split + 2);
        let event = "message";
        const data: string[] = [];
        for (const line of frame.split("\n")) {
          if (line.startsWith("event:")) event = line.slice(6).trim();
          else if (line.startsWith("data:")) data.push(line.slice(5).trim());
        }
        if (data.length === 0) continue;
        yield { event, data: JSON.parse(data.join("\n")) };
      }
    }
  } finally {
    // Releases the connection when the caller stops early — a reader who
    // navigates away mid-answer, which the server treats as a cancellation.
    await reader.cancel().catch(() => undefined);
  }
}

export const api = {
  get: <T>(path: string, query?: Record<string, unknown>, signal?: AbortSignal) =>
    request<T>(path, { query, signal }),
  post: <T>(path: string, body?: unknown, query?: Record<string, unknown>) =>
    request<T>(path, { method: "POST", body, query }),
  patch: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: "PATCH", body }),
  delete: <T>(path: string) => request<T>(path, { method: "DELETE" }),
  /** Absolute URL for an <img> or a download, with the token in the query. */
  assetUrl(path: string, query?: Record<string, unknown>) {
    const url = new URL(buildUrl(path, query));
    return url.toString();
  },
  /**
   * Blob fetch for images and downloads. Uses the same auth path as everything
   * else rather than putting a token in a URL, which would leak it into
   * browser history and any proxy log.
   */
  async blob(path: string, query?: Record<string, unknown>): Promise<string> {
    const response = await request<Response>(path, { query, raw: true });
    return URL.createObjectURL(await response.blob());
  },
  baseUrl: BASE,
};

// ---------------------------------------------------------------- API types

export type UserSummary = {
  id: string;
  displayName: string;
  email: string;
  initials: string;
  avatarColour: string;
};

export type Me = {
  id: string;
  email: string;
  displayName: string;
  initials: string;
  avatarColour: string;
  role: string;
  roleLabel: string;
  roleArticleLabel: string;
  categories: string[];
  capabilities: string[];
  canUpload: boolean;
  canReview: boolean;
  canPublish: boolean;
  canManage: boolean;
  canSeeDrafts: boolean;
  canViewAudit: boolean;
};

export type TokenResponse = {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
  user: Me;
};

export type AccountOption = {
  email: string;
  displayName: string;
  initials: string;
  role: string;
  roleLabel: string;
  capabilitySummary: string;
  avatarColour: string;
};

export type LoginOptions = {
  passwordLogin: boolean;
  externalLogin: boolean;
  externalLabel: string;
  accounts: AccountOption[];
  dataResidencyWarning: boolean;
};

export type DocumentSummary = {
  id: string;
  docNo: string;
  title: string;
  status: string;
  statusLabel: string;
  category: string;
  categoryLabel: string;
  versionLabel: string;
  pageCount: number;
  owner: UserSummary | null;
  createdAt: string;
  updatedAt: string;
  nextReviewDate: string | null;
  isRestricted: boolean;
  reviewState: "none" | "due" | "overdue";
  daysUntilReview: number | null;
  lockedBy: string | null;
  hasConflict: boolean;
};

export type VersionSummary = {
  id: string;
  versionLabel: string;
  etag: string;
  size: number;
  pageCount: number;
  chunkCount: number;
  changeNote: string;
  isArchived: boolean;
  isCurrent: boolean;
  /** The approved version everyone else, and the assistant, reads. */
  isPublished: boolean;
  /** "source" (from SharePoint) or "draft" (saved in the DMS). */
  origin: string;
  createdAt: string;
  ocrApplied: boolean;
  ocrNeedsCheck: boolean;
  ocrConfidence: number | null;
  extractorName: string;
  indexedAt: string | null;
  warnings: string[];
};

export type WorkflowActionOption = {
  action: string;
  label: string;
  targetStatus: string;
  targetStatusLabel: string;
  requiresComment: boolean;
  /** Collect reviewers and an approver first; a bare transition is refused. */
  needsRoster: boolean;
};

export type DocumentDetail = DocumentSummary & {
  description: string;
  retentionLabel: string;
  accessLabel: string;
  sourceFilename: string;
  sharepointItemId: string | null;
  currentVersion: VersionSummary | null;
  versions: VersionSummary[];
  properties: { key: string; value: string }[];
  lock: {
    heldBy: UserSummary;
    acquiredAt: string;
    expiresAt: string;
    isMine: boolean;
    sharepointCheckedOut: boolean;
  } | null;
  conflict: {
    id: string;
    localEtag: string;
    remoteEtag: string;
    detectedAt: string;
  } | null;
  availableActions: WorkflowActionOption[];
  canEdit: boolean;
  canComment: boolean;
  canCheckOut: boolean;
  /** Holds the check-out, the document is a draft, and the editor opens it. */
  canEditContent: boolean;
  /** The file in SharePoint; empty for a draft never published. */
  webUrl: string;
  publishedVersionId: string | null;
};

export type SavedViewSummary = {
  key: string;
  label: string;
  dotColour: string;
  count: number;
};

export type DocumentListResponse = {
  items: DocumentSummary[];
  total: number;
  page: number;
  pageSize: number;
  pageCount: number;
  views: SavedViewSummary[];
};

export type VersionDiffLine = {
  /** "+" added, "-" removed, empty for context. */
  mark: string;
  text: string;
};

export type VersionDiff = {
  fromLabel: string;
  toLabel: string;
  additions: number;
  removals: number;
  lines: VersionDiffLine[];
};

export type SearchHit = {
  documentId: string;
  chunkId: string;
  docNo: string;
  title: string;
  status: string;
  statusLabel: string;
  category: string;
  categoryLabel: string;
  versionLabel: string;
  pageFrom: number;
  pageTo: number;
  sectionHeading: string;
  snippet: string;
  highlight: string;
  score: number;
  matchPercent: number;
  matchedKeyword: boolean;
  matchedVector: boolean;
  /** The passage includes text OCR read out of a diagram. */
  fromDiagram: boolean;
};

export type CountedValue = { value: string; label: string; count: number };

export type SearchResponse = {
  query: string;
  hits: SearchHit[];
  documentCount: number;
  /** Whether any hit contains the query's actual words. */
  lexicalMatch: boolean;
  categories: CountedValue[];
  statuses: CountedValue[];
};

/**
 * The layout a segment declares, read server-side off the markdown the model
 * wrote and stripped out of `text`. Anything unrecognised renders as prose.
 */
export type SegmentKind = "prose" | "heading" | "step" | "bullet" | "key_value";

export type AnswerSegment = {
  text: string;
  citation: number | null;
  /**
   * Character ranges in `text` worth emphasising — a point name, a threshold, a
   * document number. Ranges rather than markup, so there is nothing to parse
   * and nothing to sanitise. Derived server-side from the cited passage.
   */
  emphasis: [number, number][];
  /**
   * Optional for the same reason `emphasis` is guarded before it is read: a
   * message stored before segments declared their layout has neither field,
   * and must render exactly as it always did.
   */
  kind?: SegmentKind;
  /** On a step, the number the source document uses. Never renumbered. */
  ordinal?: number | null;
};

export type Citation = {
  n: number;
  documentId: string;
  docNo: string;
  title: string;
  version: string;
  pageFrom: number;
  pageTo: number;
  snippet: string;
  locator: string;
  /** Addresses the rendered page; page images are keyed by version. */
  versionId: string | null;
  /** Part of the passage was read out of a picture by OCR. */
  fromDiagram: boolean;
  /**
   * How deep in the ranking retrieval found this passage. Distinct from `n`,
   * which is what the answer calls it — sources are numbered 1…N for a reader,
   * and this is the provenance underneath that numbering.
   */
  retrievalRank: number;
};

export type AnswerResponse = {
  answer: AnswerSegment[];
  citations: Citation[];
  model: string;
  tokensIn: number;
  tokensOut: number;
  latencyMs: number;
  refused: boolean;
  messageId: string;
  threadId: string;
  chunksUsed: number;
  followUps: string[];
  /**
   * The standalone question the answer was produced from, when a follow-up was
   * expanded. Null when the question was used as typed.
   */
  resolvedQuestion: string | null;
};

export type ThreadSummary = {
  id: string;
  title: string;
  lastMessageAt: string | null;
  createdAt: string;
  messageCount: number;
  /** The last question asked, for the preview line under the title. */
  lastQuestion: string | null;
};

export type RetrievalScopeOption = {
  id: string;
  label: string;
  count: number;
  available: boolean;
};

export type MetricBar = {
  label: string;
  value: string;
  pct: number;
  colour: string;
  note: string;
};

export type StatTile = { key: string; value: string; tone: string };

export type HomeResponse = {
  greeting: string;
  firstName: string;
  roleLabel: string;
  kpis: { label: string; value: string; trend: string; tone: string }[];
  needsAttention: DocumentSummary[];
  recentlyPublished: DocumentSummary[];
  indexHealth: MetricBar[];
  suggestedQuestions: string[];
  documentCount: number;
  unreadNotifications: number;
  residencyWarning: boolean;
};

export type TaskItem = {
  document: DocumentSummary;
  stageName: string;
  assignee: string | null;
  dueAt: string | null;
  slaState: "on_track" | "due_soon" | "overdue";
  slaLabel: string;
  changeNote: string;
  primaryAction: string | null;
  primaryActionLabel: string | null;
};

export type TaskQueueResponse = {
  items: TaskItem[];
  summary: { key: string; value: string }[];
};

export type BoardColumn = {
  status: string;
  label: string;
  count: number;
  items: DocumentSummary[];
};

export type WorkflowTemplate = {
  id: string;
  name: string;
  category: string | null;
  categoryLabel: string;
  slaDays: number;
  stages: { name: string; role: string; slaDays: number }[];
  isActive: boolean;
  runsThisYear: number;
};

export type WorkflowRule = {
  key: string;
  label: string;
  detail: string;
  isEnabled: boolean;
};

export type NotificationRow = {
  id: string;
  text: string;
  dotColour: string;
  targetKind: string;
  targetId: string | null;
  createdAt: string;
  readAt: string | null;
};

export type CommentRow = {
  id: string;
  author: UserSummary | null;
  authorRole: string;
  body: string;
  createdAt: string;
  resolvedAt: string | null;
  isChangeRequest: boolean;
  versionLabel: string | null;
};

export type ActivityRow = {
  id: string;
  actor: string;
  initials: string;
  summary: string;
  type: string;
  occurredAt: string;
};

export type UserRow = {
  id: string;
  displayName: string;
  email: string;
  initials: string;
  avatarColour: string;
  role: string;
  roleLabel: string;
  categories: string[];
  scopeLabel: string;
  lastActiveAt: string | null;
  isActive: boolean;
  sourceGroup: string | null;
};

export type SyncStatus = {
  provider: string;
  siteLabel: string;
  isOnline: boolean;
  deltaToken: string | null;
  lastRunAt: string | null;
  lastRunStatus: string;
  lastError: string | null;
  nextRunAt: string | null;
  itemsPending: number;
  failures24h: number;
  webhookSubscriptionId: string | null;
  webhookExpiresAt: string | null;
  librariesWatched: number;
  authMode: string;
  supportsWebhooks: boolean;
  supportsCheckout: boolean;
};

export type QueueItem = {
  id: string;
  filename: string;
  stage: string;
  stageLabel: string;
  progress: number;
  pageCount: number;
  chunkCount: number;
  ocrNeedsCheck: boolean;
  size: number;
  updatedAt: string;
};

export type IngestionOverview = {
  inProgress: QueueItem[];
  completedToday: QueueItem[];
  completedCount: number;
  chunksToday: number;
  failed: {
    id: string;
    filename: string;
    error: string;
    attempts: number;
    updatedAt: string;
  }[];
  extractionQuality: MetricBar[];
  indexHealth: MetricBar[];
};

export type UsageSummary = {
  range: string;
  queries: number;
  tokensIn: number;
  tokensOut: number;
  averageTokens: number;
  estimatedCost: number;
  tokenCoveragePct: number;
  bars: number[];
  barLabels: string[];
  byModel: { model: string; queries: number; tokens: number; cost: number }[];
};

export type EvalCaseMetric = {
  key: string;
  label: string;
  shortLabel: string;
  /** null = not measured. 0 = measured and scored nothing. Never conflate them. */
  value: number | null;
  display: string;
  detail: string;
  pct: number;
  /** "good" | "bad" | "unmeasured" */
  tone: string;
  /** "target ≥ 90%" — the floor this metric is judged against, not a shared one. */
  target: string;
  /** What the metric asks. `detail` is what this case counted. */
  explanation: string;
};

export type EvalHeadline = {
  key: string;
  label: string;
  value: string;
  caption: string;
  tone: string;
};

export type EvalCase = {
  caseId: string;
  question: string;
  expected: string;
  expectRefusal: boolean;
  mustCiteDocNo: string | null;
  hasGroundTruth: boolean;
  passed: boolean | null;
  reason: string | null;
  refused: boolean | null;
  /** Tri-state: null means the check does not apply, not that it failed. */
  hasCitation: boolean | null;
  citedExpectedDocument: boolean | null;
  refusedCorrectly: boolean | null;
  latencyMs: number | null;
  citations: string[];
  citationCount: number;
  retrievedCount: number | null;
  contextCount: number | null;
  promptVersion: number | null;
  metrics: EvalCaseMetric[];
  judgeCalls: number | null;
  judgeError: string | null;
  /** Null unless the viewer can see drafts — the harness retrieves in that scope. */
  answerText: string | null;
  contextLocators: string[];
};

export type EvalSummary = {
  runId: string | null;
  model: string;
  startedAt: string | null;
  passCount: number;
  total: number;
  passRate: number;
  metrics: MetricBar[];
  promotionBlocked: boolean;
  cases: EvalCase[];
  headline: EvalHeadline[];
  /** A run is in flight; the figures above are from the last finished one. */
  running: boolean;
};

export type ResponseRow = {
  messageId: string;
  askedAt: string;
  question: string;
  resolvedQuestion: string | null;
  /** A role label, not a name, unless the server is configured otherwise. */
  author: string;
  scope: string[];
  model: string | null;
  latencyMs: number;
  tokensIn: number;
  tokensOut: number;
  chunksUsed: number;
  citationCount: number;
  refused: boolean;
  refusalReason: string | null;
  /** The reader's own verdict, "up" or "down". */
  feedback: string | null;
  /** null = never scored. */
  scoreState: string | null;
  scoredAt: string | null;
  scoreError: string | null;
  anyBelowFloor: boolean | null;
  /** "full" | "unrecorded" | "reindexed" | "none" */
  contextState: string;
  contextNote: string;
  metrics: EvalCaseMetric[];
  judgeCalls: number | null;
  answerText: string | null;
  contextLocators: string[];
};

export type ResponseListResponse = {
  items: ResponseRow[];
  total: number;
  page: number;
  pageSize: number;
  pageCount: number;
  answeredTotal: number;
  scoredTotal: number;
  pendingTotal: number;
  refusedTotal: number;
  judgeBlocked: string | null;
  judgeNotes: string[];
  usedToday: number;
  dailyLimit: number;
};

export type ResponseScoreView = {
  messageId: string;
  state: string | null;
  scoredAt: string | null;
  error: string | null;
  anyBelowFloor: boolean | null;
  contextState: string;
  contextNote: string;
  judgeCalls: number;
  tokensIn: number;
  tokensOut: number;
  metrics: EvalCaseMetric[];
};

export type AuditResponse = {
  items: {
    id: string;
    occurredAt: string;
    type: string;
    summary: string;
    actor: string;
    subjectId: string | null;
  }[];
  total: number;
  allTimeTotal: number;
  breakdown: MetricBar[];
  retentionYears: number;
};

export type OpsResponse = {
  stats: StatTile[];
  providerHealth: Record<string, boolean>;
  residencyWarning: boolean;
  residencyDetail: string;
  integrations: { name: string; detail: string; state: string }[];
};

export type VersionInfo = {
  name: string;
  environment: string;
  build: string;
  model: string;
  providers: Record<string, string>;
  dataResidencyWarning: boolean;
  /**
   * A trace carries the question, the answer and every retrieved passage in
   * one payload, which the provider call does not — so this is its own flag,
   * not a shade of the one above.
   */
  traceResidencyWarning: boolean;
  /** Passages placed in the answer prompt, reported rather than assumed. */
  retrievalTopK: number;
  /**
   * The cosine similarity below which a passage with no keyword match is
   * refused. Reported for the same reason as the passage budget: an
   * administrator can change it, so the panel must not state a literal.
   */
  relevanceFloor: number;
  citationsRequired: boolean;
};

export type SettingRow = {
  key: string;
  group: string;
  label: string;
  detail: string;
  /** The long argument for the value, shown in the info dot. */
  rationale: string | null;
  kind: "int" | "float" | "bool" | "enum";
  /** In force: the override if there is one, otherwise `configured`. */
  value: number | boolean | string;
  /** What this deployment's environment resolved at boot. */
  configured: number | boolean | string;
  /** What the code says, with no environment and no override. */
  codeDefault: number | boolean | string;
  isOverridden: boolean;
  /** The environment moved this away from the code default. */
  configuredFromEnvironment: boolean;
  minimum: number | null;
  maximum: number | null;
  step: number | null;
  options: string[];
  unit: string | null;
  /** Non-null means read-only, and this is the reason and the remedy. */
  locked: string | null;
  appliesTo:
    | "immediately"
    | "next_question"
    | "next_answer"
    | "next_ingestion"
    | "restart";
  updatedAt: string | null;
  updatedBy: string | null;
};

export type SettingGroup = {
  key: string;
  label: string;
  detail: string;
  footer: string | null;
  settings: SettingRow[];
};

export type SettingsResponse = {
  groups: SettingGroup[];
  /** How long a change takes to reach the worker containers. */
  propagationSeconds: number;
};

export type AssignableUser = {
  id: string;
  displayName: string;
  email: string;
  initials: string;
  avatarColour: string;
  role: string;
  roleLabel: string;
  scopeLabel: string;
  /** Decisions this person already owes. */
  openAssignments: number;
};

export type AssignableUsers = {
  reviewers: AssignableUser[];
  approvers: AssignableUser[];
  suggestedApproverId: string | null;
};

export type AssignmentRow = {
  userId: string;
  displayName: string;
  initials: string;
  avatarColour: string;
  role: string;
  /** "PENDING" | "APPROVED" | "CHANGES_REQUESTED" | "REJECTED" */
  decision: string;
  decidedAt: string | null;
  comment: string | null;
};

export type AssignmentsResponse = {
  roundNo: number;
  reviewers: AssignmentRow[];
  approver: AssignmentRow | null;
  approved: number;
  total: number;
  /** Non-null when the viewer is a named reviewer on this round. */
  myDecision: string | null;
};

export type ReviewDecisionResponse = {
  approved: number;
  total: number;
  advanced: boolean;
  returned: boolean;
  status: string;
  statusLabel: string;
  message: string;
};
